import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import {
  ExceptionReportScope,
  ExceptionReportScopeValues,
  ToleranceAreaValues,
  type ProjectGovernanceDto,
  type RaiseExceptionReportDto,
  type ToleranceArea,
} from '@paladin-monorepo/common';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { projectGovernanceApi } from '@/lib/api/project-governance';
import { useGovernanceMutation } from './useGovernanceMutation';

export function ExceptionReportForm({
  projectId,
  governance,
}: {
  projectId: string;
  governance: ProjectGovernanceDto;
}) {
  const { t } = useTranslation();
  const schema = yup
    .object({
      scope: yup.mixed<ExceptionReportScope>().oneOf(ExceptionReportScopeValues).required(),
      cause: yup.string().trim().required(t('workspace.governance.exceptionReport.required')),
      impact: yup.string().trim().required(t('workspace.governance.exceptionReport.required')),
      threatenedAreas: yup
        .array()
        .of(yup.mixed<ToleranceArea>().oneOf(ToleranceAreaValues).required())
        .min(1, t('workspace.governance.exceptionReport.areasRequired'))
        .required(),
      options: yup.string().trim().required(t('workspace.governance.exceptionReport.required')),
      recommendation: yup.string().trim().required(t('workspace.governance.exceptionReport.required')),
      proposedBaselineBudget: yup
        .string()
        .defined()
        .test('num', t('workspace.governance.baselineBudget.invalid'), (v) => !v || Number(v) >= 0),
    })
    .required();
  const { register, control, handleSubmit, reset, formState } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      scope: ExceptionReportScope.Stage,
      cause: '',
      impact: '',
      threatenedAreas: [] as ToleranceArea[],
      options: '',
      recommendation: '',
      proposedBaselineBudget: '',
    },
  });
  const mutation = useGovernanceMutation(projectId, (dto: RaiseExceptionReportDto) =>
    projectGovernanceApi.raiseExceptionReport(projectId, dto),
    () => reset(),
  );

  return (
    <form
      className="space-y-2 rounded-md border border-border p-3"
      onSubmit={handleSubmit((v) =>
        mutation.mutate({
          expectedVersion: governance.version,
          scope: v.scope,
          cause: v.cause,
          impact: v.impact,
          threatenedAreas: v.threatenedAreas as ToleranceArea[],
          options: v.options,
          recommendation: v.recommendation,
          proposedBaselineBudget: v.proposedBaselineBudget === '' ? null : Number(v.proposedBaselineBudget),
        }),
      )}
    >
      <h5 className="text-sm font-medium">{t('workspace.governance.exceptionReport.title')}</h5>
      <div className="space-y-1">
        <Label>{t('workspace.governance.exceptionReport.scope')}</Label>
        <Controller
          control={control}
          name="scope"
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ExceptionReportScopeValues.map((scope) => (
                  <SelectItem key={scope} value={scope}>
                    {t(`workspace.governance.exceptionReport.scopeValue.${scope}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="exception-cause">{t('workspace.governance.exceptionReport.cause')}</Label>
        <Textarea id="exception-cause" rows={2} {...register('cause')} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="exception-impact">{t('workspace.governance.exceptionReport.impact')}</Label>
        <Textarea id="exception-impact" rows={2} {...register('impact')} />
      </div>
      <fieldset className="space-y-1">
        <legend className="text-sm font-medium">{t('workspace.governance.exceptionReport.areas')}</legend>
        <div className="flex flex-wrap gap-2">
          {ToleranceAreaValues.map((area) => (
            <label key={area} className="flex items-center gap-1 text-xs">
              <input type="checkbox" value={area} {...register('threatenedAreas')} />
              {t(`workspace.controls.area.${area}`)}
            </label>
          ))}
        </div>
        {formState.errors.threatenedAreas && (
          <p className="text-xs text-destructive">{formState.errors.threatenedAreas.message}</p>
        )}
      </fieldset>
      <div className="space-y-1">
        <Label htmlFor="exception-options">{t('workspace.governance.exceptionReport.options')}</Label>
        <Textarea id="exception-options" rows={2} {...register('options')} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="exception-recommendation">{t('workspace.governance.exceptionReport.recommendation')}</Label>
        <Textarea id="exception-recommendation" rows={2} {...register('recommendation')} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="exception-budget">{t('workspace.governance.exceptionReport.proposedBudget')}</Label>
        <Input id="exception-budget" type="number" min={0} step="0.01" {...register('proposedBaselineBudget')} />
      </div>
      {(formState.errors.cause || formState.errors.impact || formState.errors.options || formState.errors.recommendation) && (
        <p className="text-xs text-destructive">{t('workspace.governance.exceptionReport.required')}</p>
      )}
      <Button type="submit" size="sm" disabled={mutation.isPending}>
        {t('workspace.governance.raiseException')}
      </Button>
    </form>
  );
}
